import {
  TrashIcon,
  ArrowPathIcon,
  CircleStackIcon,
  Square3Stack3DIcon,
  InformationCircleIcon,
  PencilIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CameraIcon,
  ChatBubbleLeftEllipsisIcon,
  EyeIcon,
  BuildingOffice2Icon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { MARKUP_COLORS } from "./drawingViewerConstants";
import {
  checklistItemStatus,
  deviationStatusLabel,
  measureLabel,
} from "./drawingAnnotationUtils";
import { DrawingStructureTree } from "./drawing-structure-tree";
import { formatDrawingLocation } from "./drawingStructureApi";
import {
  isInspectableModule,
  parseModuleMeta,
} from "./drawingObservationUtils";

const MODULE_ICONS = {
  arrow: ArrowPathIcon,
  circle: CircleStackIcon,
  rectangular: Square3Stack3DIcon,
  pen: PencilIcon,
  measure: ScaleIcon,
  photo: CameraIcon,
  note: ChatBubbleLeftEllipsisIcon,
};

const TAB_ICONS = {
  modules: Square3Stack3DIcon,
  deviations: ExclamationTriangleIcon,
  checklists: ClipboardDocumentCheckIcon,
  structure: BuildingOffice2Icon,
  details: InformationCircleIcon,
};

/** Icon-only tabs; full label via title, aria-label, and hover/focus tooltip. */
const DrawingViewerTabBar = ({ tabs, activeTab, onTabChange }) => (
  <div
    role="tablist"
    className="dv-tabbar relative z-10 flex items-stretch gap-0.5 overflow-visible border-b border-[#e5e8ec] px-1.5 pt-1.5"
  >
    {tabs.map(({ id, label, count }) => {
      const active = activeTab === id;
      const Icon = TAB_ICONS[id] || Square3Stack3DIcon;
      const hasCount = count !== null && count !== undefined;

      return (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={active}
          aria-label={label}
          title={label}
          onClick={() => onTabChange(id)}
          className={`group relative mb-[-1px] flex h-9 min-w-0 flex-1 items-center justify-center border-b-2 px-0.5 text-[12px] font-semibold transition-colors ${
            active
              ? "border-[#2b6ef0] text-[#2b6ef0]"
              : "border-transparent text-[#59616d] hover:text-[#14171c]"
          }`}
        >
          <Icon className="h-[18px] w-[18px] flex-shrink-0" aria-hidden />
          {hasCount && (
            <span
              className={`absolute right-0.5 top-0.5 grid min-h-[14px] min-w-[14px] place-items-center rounded-full px-0.5 font-mono text-[9px] leading-none ${
                active ? "bg-[#eaf1fe] text-[#1d4fb0]" : "bg-[#eef0f3] text-[#59616d]"
              }`}
            >
              {count}
            </span>
          )}
          <span
            role="tooltip"
            className="pointer-events-none absolute left-1/2 top-full z-30 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#14171c] px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-[0_8px_20px_rgba(20,23,28,0.28)] transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          >
            {label}
          </span>
        </button>
      );
    })}
  </div>
);

export const DrawingViewerMarkupBar = ({ t, color, onColorChange, visible }) => {
  if (!visible) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 top-3.5 z-[25] flex justify-center px-4">
      <div
        data-viewer-ui
        className="pointer-events-auto flex items-center gap-2 rounded-xl border border-[#e5e8ec] bg-white px-2.5 py-1.5 shadow-[0_12px_34px_rgba(20,23,28,0.18),0_2px_8px_rgba(20,23,28,0.10)]"
        onPointerDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#59616d]">
          {t("drawings.module.color")}
        </span>
        <div className="flex items-center gap-1.5">
          {MARKUP_COLORS.map((swatch) => (
            <button
              key={swatch.id}
              type="button"
              onClick={() => onColorChange(swatch.val)}
              className={`h-[22px] w-[22px] rounded-full border-2 border-white transition-transform hover:scale-110 ${
                color === swatch.val
                  ? "shadow-[0_0_0_2px_#fff,0_0_0_4px_#2b6ef0]"
                  : "shadow-[0_0_0_1px_#d6dae0]"
              }`}
              style={{ background: swatch.val }}
              title={swatch.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const DrawingViewerCanvasDock = ({
  t,
  zoom,
  page,
  pageCount,
  onZoomChange,
  onFit,
  onPageChange,
}) => {
  const btnClass =
    "grid h-10 min-w-10 place-items-center rounded-[8px] border-none bg-transparent px-1.5 font-mono text-xs text-[#cdd3dc] hover:bg-white/10 hover:text-white active:bg-white/15 disabled:opacity-35 xl:h-[30px] xl:min-w-[30px] xl:rounded-[7px] xl:px-2";

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--mobile-nav-height,3.75rem)+env(safe-area-inset-bottom,0px)+0.35rem)] z-[56] flex max-w-full justify-center px-2 xl:hidden">
      <div
        data-viewer-ui
        className="pointer-events-auto flex max-w-full items-center gap-0 rounded-[12px] border border-white/10 bg-[rgba(20,23,28,0.88)] p-0.5 shadow-[0_12px_34px_rgba(20,23,28,0.22)] backdrop-blur-[10px]"
        onPointerDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onZoomChange?.(zoom * 0.8);
          }}
          className={btnClass}
          title={t("drawings.viewer.zoom_out")}
          aria-label={t("drawings.viewer.zoom_out")}
        >
          −
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onFit?.();
          }}
          className={`${btnClass} min-w-[40px] xl:min-w-[54px]`}
          title={t("drawings.viewer.fit_to_screen")}
          aria-label={`${t("drawings.viewer.fit_to_screen")} (${Math.round(zoom * 100)}%)`}
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onZoomChange?.(zoom * 1.25);
          }}
          className={btnClass}
          title={t("drawings.viewer.zoom_in")}
          aria-label={t("drawings.viewer.zoom_in")}
        >
          +
        </button>

        {pageCount > 1 && (
          <>
            <div className="mx-0.5 h-4 w-px bg-white/12 xl:h-[18px]" />
            <button
              type="button"
              disabled={page <= 0}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onPageChange?.(page - 1);
              }}
              className={btnClass}
              title={t("drawings.viewer.prev_page")}
              aria-label={t("drawings.viewer.prev_page")}
            >
              ‹
            </button>
            <div className="grid h-10 min-w-[40px] place-items-center rounded-[8px] px-1.5 font-mono text-xs font-semibold text-[#cdd3dc] xl:h-[30px]">
              {page + 1}/{pageCount}
            </div>
            <button
              type="button"
              disabled={page >= pageCount - 1}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onPageChange?.(page + 1);
              }}
              className={btnClass}
              title={t("drawings.viewer.next_page")}
              aria-label={t("drawings.viewer.next_page")}
            >
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const DrawingViewerPanel = ({
  t,
  drawing,
  modules,
  selectedModule,
  pendingChanges,
  panelOpen,
  tab,
  onTabChange,
  onSelectModule,
  onDeleteModule,
  onCenterModule,
  deviations = [],
  checklists = [],
  relatedLoading = false,
  measureScale,
  onPinDeviation,
  onStartCalibration,
  onCreateVersion,
  onSwitchVersion,
  activeVersionId,
  versions = [],
  creatingVersion = false,
  expandedChecklistId,
  onToggleChecklist,
  draftDrawingFloorId,
  setDraftDrawingFloorId,
  locationDirty,
  savingLocation,
  onSaveLocation,
  onOpenModuleDetail,
  onAddChecklist,
  onLinkChecklist,
  onFillChecklist,
  embedded = false,
  hideModulesTab = false,
  canEdit = true,
}) => {
  const tabs = [
    {
      id: "modules",
      label: t("drawings.viewer.modules"),
      count: modules.length,
    },
    {
      id: "deviations",
      label: t("drawings.viewer.deviations"),
      count: deviations.length,
    },
    {
      id: "checklists",
      label: t("drawings.viewer.checklists"),
      count: checklists.length,
    },
    {
      id: "structure",
      label: t("drawings.structure.title"),
      count: null,
    },
    {
      id: "details",
      label: t("drawings.viewer.details"),
      count: null,
    },
  ].filter((item) => !(hideModulesTab && item.id === "modules"));

  const pinnedDeviationIds = new Set(
    modules.filter((module) => module.deviation_id).map((module) => module.deviation_id)
  );

  return (
    <aside
      className={
        embedded
          ? "flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white"
          : `min-h-0 w-[328px] flex-shrink-0 flex-col overflow-hidden border-l border-[#e5e8ec] bg-white transition-transform max-xl:hidden ${
              panelOpen
                ? "flex max-lg:fixed max-lg:inset-y-0 max-lg:right-0 max-lg:z-40 max-lg:shadow-[0_24px_60px_rgba(15,23,42,0.18)]"
                : "hidden"
            }`
      }
    >
      <DrawingViewerTabBar
        tabs={tabs}
        activeTab={tab}
        onTabChange={onTabChange}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === "modules" && (
          <>
            <div className="flex items-center justify-between px-3.5 pb-2 pt-3">
              <h3 className="text-xs font-bold uppercase tracking-[0.05em] text-[#59616d]">
                {t("drawings.viewer.modules")}
              </h3>
            </div>
            <p className="px-3.5 pb-3 text-[11.5px] leading-relaxed text-[#59616d]">
              {t("drawings.viewer.tool_hint")}
            </p>

            {modules.length === 0 ? (
              <div className="flex flex-col items-center gap-2.5 px-6 py-10 text-center text-[#969da8]">
                <Square3Stack3DIcon className="h-8 w-8 opacity-40" />
                <p className="m-0 text-[13px] leading-relaxed">
                  {t("drawings.viewer.no_modules")}
                </p>
              </div>
            ) : (
              <ul>
                {modules.map((module, index) => {
                  const Icon = MODULE_ICONS[module.module_type] || Square3Stack3DIcon;
                  const isSelected = selectedModule?.id === module.id;
                  const hasPending = !!pendingChanges[module.id];
                  const meta = parseModuleMeta(module);
                  const inspectable = isInspectableModule(module);
                  const moduleTitle =
                    meta.title ||
                    module.deviation?.displayTitle ||
                    module.deviation?.title ||
                    t(`drawings.module.${module.module_type}`);

                  return (
                    <li key={module.id}>
                      <div
                        className={`group relative flex cursor-pointer items-center gap-2.5 border-b border-[#e5e8ec] px-3.5 py-2.5 transition-colors hover:bg-[#f6f7f9] ${
                          isSelected ? "bg-[#eaf1fe]" : ""
                        }`}
                      >
                        {isSelected && (
                          <span className="absolute bottom-0 left-0 top-0 w-[3px] bg-[#2b6ef0]" />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            onSelectModule(module);
                            onCenterModule(module);
                          }}
                          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                        >
                          <span
                            className="grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-lg bg-[#eef0f3]"
                            style={{ color: module.color || "#14171c" }}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#14171c]">
                              <span
                                className="inline-block h-2 w-2 flex-shrink-0 rounded-[3px]"
                                style={{ background: module.color || "#14171c" }}
                              />
                              {moduleTitle}
                            </span>
                            <span className="mt-0.5 block font-mono text-[11px] text-[#59616d]">
                              {module.module_type === "measure"
                                ? `${measureLabel(
                                    parseFloat(module.x_position) || 0,
                                    parseFloat(module.y_position) || 0,
                                    parseFloat(module.x2_position) || 0,
                                    parseFloat(module.y2_position) || 0,
                                    measureScale
                                  )} mm`
                                : module.module_type === "pen"
                                  ? t("drawings.module.pen")
                                  : typeof meta.page === "number"
                                    ? `${t("drawings.observation.page")} ${meta.page + 1}`
                                    : module.deviation_id
                                      ? `#${module.deviation?.number || module.deviation_id}`
                                      : `x${Math.round(module.x_position || 0)} y${Math.round(module.y_position || 0)}`}
                              {hasPending ? ` · ${t("drawings.viewer.unsaved")}` : ""}
                            </span>
                          </span>
                        </button>
                        {inspectable && (
                          <button
                            type="button"
                            onClick={() => onOpenModuleDetail?.(module)}
                            className="grid h-[26px] w-[26px] flex-shrink-0 place-items-center rounded-[6px] text-[#59616d] opacity-0 transition-opacity hover:bg-[#eaf1fe] hover:text-[#2b6ef0] group-hover:opacity-100"
                            title={t("drawings.observation.view_details")}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => onDeleteModule(module.id)}
                            className="grid h-[26px] w-[26px] flex-shrink-0 place-items-center rounded-[6px] text-[#969da8] opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                            title={t("drawings.module.delete")}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                        <span className="absolute right-3.5 top-2 font-mono text-[10px] text-[#969da8]">
                          #{index + 1}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {tab === "deviations" && (
          <div className="px-3.5 py-3">
            {!drawing.project?.id ? (
              <p className="text-[13px] text-[#59616d]">
                {t("drawings.viewer.no_project_linked")}
              </p>
            ) : relatedLoading ? (
              <p className="text-[13px] text-[#59616d]">{t("Loading...")}</p>
            ) : deviations.length === 0 ? (
              <p className="text-[13px] text-[#59616d]">
                {t("drawings.viewer.no_deviations")}
              </p>
            ) : (
              <ul className="space-y-2">
                {deviations.map((deviation) => {
                  const pinned = pinnedDeviationIds.has(deviation.id);
                  const linkedModule = modules.find(
                    (module) => module.deviation_id === deviation.id
                  );
                  const openInDialog = () => {
                    if (linkedModule) {
                      onOpenModuleDetail?.(linkedModule);
                      return;
                    }
                    // Unpinned: open rich in-context dialog with a synthetic module.
                    onOpenModuleDetail?.({
                      id: `deviation-preview-${deviation.id}`,
                      deviation_id: deviation.id,
                      deviation,
                      module_type: "circle",
                      color: "#e5484d",
                      path_data: JSON.stringify({
                        kind: "deviation",
                        title: deviation.displayTitle || deviation.title,
                      }),
                      _previewOnly: true,
                    });
                  };

                  return (
                    <li
                      key={deviation.id}
                      className="rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-3"
                    >
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#e05252]" />
                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={openInDialog}
                            className="w-full text-left text-[13px] font-semibold text-[#14171c] hover:text-[#2b6ef0]"
                          >
                            #{deviation.number}{" "}
                            {deviation.displayTitle || deviation.title}
                          </button>
                          <p className="mt-1 text-[11px] text-[#59616d]">
                            {deviationStatusLabel(deviation.status, t)}
                          </p>
                        </div>
                        <Link
                          to={`/dashboard/hse-ks/deviations/${deviation.id}`}
                          className="grid h-8 w-8 flex-none place-items-center rounded-[7px] border border-[#e5e8ec] bg-white text-[#2b6ef0] hover:bg-[#eaf1fe]"
                          title={t("drawings.observation.open_deviation_page")}
                          aria-label={t("drawings.observation.open_deviation_page")}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </Link>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={openInDialog}
                          className="inline-flex items-center gap-1 rounded-[7px] border border-[#2b6ef0]/20 bg-[#eaf1fe] px-2 py-1 text-[11px] font-semibold text-[#1d4fb0]"
                        >
                          {t("drawings.viewer.open_deviation_dialog")}
                        </button>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => onPinDeviation(deviation.id)}
                            className="inline-flex items-center gap-1 rounded-[7px] border border-[#e5e8ec] bg-white px-2 py-1 text-[11px] font-semibold text-[#14171c] hover:bg-[#eef0f3]"
                          >
                            <MapPinIcon className="h-3.5 w-3.5" />
                            {pinned
                              ? t("drawings.viewer.repin_deviation")
                              : t("drawings.viewer.pin_deviation")}
                          </button>
                        )}
                        {linkedModule && (
                          <button
                            type="button"
                            onClick={() => {
                              onSelectModule(linkedModule);
                              onCenterModule(linkedModule);
                            }}
                            className="rounded-[7px] border border-[#2b6ef0]/20 bg-[#eaf1fe] px-2 py-1 text-[11px] font-semibold text-[#1d4fb0]"
                          >
                            {t("drawings.viewer.show_on_drawing")}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === "checklists" && (
          <div className="px-3.5 py-3">
            <p className="mb-3 text-[12px] leading-snug text-[#59616d]">
              {t("drawings.viewer.checklists_project_copy")}
            </p>
            {canEdit && drawing.project?.id && (
              <button
                type="button"
                onClick={() => onAddChecklist?.()}
                className="mb-3 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-[9px] border border-[#2b6ef0] bg-[#2b6ef0] px-3 text-[13px] font-semibold text-white"
              >
                <ClipboardDocumentCheckIcon className="h-4 w-4" />
                {t("drawings.viewer.checklist_add")}
              </button>
            )}
            {!drawing.project?.id ? (
              <p className="text-[13px] text-[#59616d]">
                {t("drawings.viewer.no_project_linked")}
              </p>
            ) : relatedLoading ? (
              <p className="text-[13px] text-[#59616d]">{t("Loading...")}</p>
            ) : checklists.length === 0 ? (
              <p className="text-[13px] text-[#59616d]">
                {t("drawings.viewer.no_checklists")}
              </p>
            ) : (
              <ul className="space-y-2">
                {checklists.map((checklist) => {
                  const items = checklist.items || [];
                  const deviationCount = items.filter((item) => item?.isDeviation).length;

                  return (
                    <li
                      key={checklist.id}
                      className="overflow-hidden rounded-[10px] border border-[#e5e8ec]"
                    >
                      <div className="flex items-stretch gap-0 bg-[#f6f7f9]">
                        <button
                          type="button"
                          onClick={() => onFillChecklist?.(checklist.id)}
                          className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2.5 text-left hover:bg-[#eef1f5]"
                        >
                          <ClipboardDocumentCheckIcon className="h-4 w-4 flex-none text-[#2b6ef0]" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-semibold text-[#14171c]">
                              #{checklist.number}{" "}
                              {checklist.displayTitle || checklist.title}
                            </span>
                            <span className="text-[11px] text-[#59616d]">
                              {items.length} {t("drawings.viewer.checklist_items")}
                              {deviationCount > 0
                                ? ` · ${deviationCount} ${t("drawings.viewer.checklist_status.deviation")}`
                                : ""}
                            </span>
                          </span>
                        </button>
                        <Link
                          to={`/dashboard/hse-ks/checklists/${checklist.id}`}
                          className="grid w-10 flex-none place-items-center border-l border-[#e5e8ec] text-[#2b6ef0] hover:bg-[#eaf1fe]"
                          title={t("drawings.viewer.open_checklist")}
                          aria-label={t("drawings.viewer.open_checklist")}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </Link>
                      </div>
                      {canEdit && (
                        <div className="flex flex-wrap items-center gap-2 border-t border-[#e5e8ec] bg-white px-3 py-2">
                          <button
                            type="button"
                            onClick={() => onLinkChecklist?.(checklist.id)}
                            className="rounded-[7px] border border-[#2b6ef0]/25 bg-[#eaf1fe] px-2 py-1 text-[11px] font-semibold text-[#1d4fb0]"
                          >
                            {t("drawings.viewer.checklist_link")}
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === "structure" && (
          <div className="space-y-4 px-3.5 py-4">
            {!drawing.project?.id ? (
              <p className="text-[13px] text-[#59616d]">
                {t("drawings.structure.project_required")}
              </p>
            ) : (
              <>
                <DrawingStructureTree
                  t={t}
                  projectId={drawing.project.id}
                  selectedFloorId={draftDrawingFloorId}
                  selectedAndre={!draftDrawingFloorId}
                  onSelectFloor={setDraftDrawingFloorId}
                  onSelectAndre={() => setDraftDrawingFloorId(null)}
                  compact
                />
                <div className="flex items-center justify-between gap-2 rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] px-3 py-2.5">
                  <div className="text-[12px] text-[#59616d]">
                    {t("drawings.structure.assign_hint")}
                  </div>
                  {canEdit && locationDirty && (
                    <button
                      type="button"
                      disabled={savingLocation}
                      onClick={onSaveLocation}
                      className="rounded-[7px] bg-[#2b6ef0] px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                    >
                      {savingLocation ? t("Saving...") : t("drawings.viewer.save_location")}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "details" && (
          <div className="space-y-4 px-3.5 py-4">
            <div className="flex items-start gap-2 rounded-[10px] bg-[#f6f7f9] px-3 py-2.5 text-[12.5px] text-[#59616d]">
              <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{drawing.description || t("drawings.viewer.no_description")}</span>
            </div>

            <div className="rounded-[10px] border border-[#e5e8ec] p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                    {t("drawings.viewer.measure_scale")}
                  </div>
                  <div className="mt-1 font-mono text-[13px] text-[#14171c]">
                    {measureScale?.toFixed(4)} mm/pt
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={onStartCalibration}
                    className="rounded-[7px] border border-[#e5e8ec] px-2.5 py-1.5 text-[11px] font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
                  >
                    {t("drawings.viewer.calibrate")}
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-[10px] border border-[#e5e8ec] p-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                {t("drawings.viewer.versions")}
              </div>
              <div className="mt-2 space-y-2">
                <button
                  type="button"
                  onClick={() => onSwitchVersion(null)}
                  className={`w-full rounded-[8px] border px-3 py-2 text-left text-[12px] font-semibold ${
                    !activeVersionId
                      ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0]"
                      : "border-[#e5e8ec] text-[#14171c] hover:bg-[#f6f7f9]"
                  }`}
                >
                  {t("drawings.viewer.live_drawing")}
                </button>
                {versions.map((version) => (
                  <button
                    key={version.id}
                    type="button"
                    onClick={() => onSwitchVersion(version.id)}
                    className={`w-full rounded-[8px] border px-3 py-2 text-left text-[12px] ${
                      activeVersionId === version.id
                        ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0]"
                        : "border-[#e5e8ec] text-[#14171c] hover:bg-[#f6f7f9]"
                    }`}
                  >
                    <div className="font-semibold">{version.title}</div>
                    <div className="mt-0.5 text-[11px] opacity-80">
                      {version.module_count || 0} {t("drawings.viewer.modules").toLowerCase()}
                    </div>
                  </button>
                ))}
              </div>
              {canEdit && (
                <button
                  type="button"
                  disabled={creatingVersion}
                  onClick={onCreateVersion}
                  className="mt-3 w-full rounded-[8px] bg-[#2b6ef0] px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
                >
                  {creatingVersion
                    ? t("Saving...")
                    : t("drawings.viewer.create_version")}
                </button>
              )}
            </div>

            {(() => {
              const { house, floor } = formatDrawingLocation(drawing);
              if (!house && !floor) return null;
              return (
                <div className="rounded-[10px] border border-[#e5e8ec] p-3">
                  <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                    {t("drawings.viewer.location")}
                  </div>
                  <div className="mt-1 text-[13px] font-medium text-[#14171c]">
                    {[house && `${t("drawings.house")} ${house}`, floor && `${t("drawings.floor")} ${floor}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              );
            })()}

            <dl className="space-y-3 text-[13px]">
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                  {t("drawings.title")}
                </dt>
                <dd className="mt-1 font-medium text-[#14171c]">{drawing.title}</dd>
              </div>
              {drawing.project?.displayTitle && (
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                    {t("drawings.project")}
                  </dt>
                  <dd className="mt-1 font-medium text-[#14171c]">
                    {drawing.project.displayTitle}
                  </dd>
                </div>
              )}
              {drawing.document?.title && (
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                    {t("drawings.document")}
                  </dt>
                  <dd className="mt-1 font-medium text-[#14171c]">
                    {drawing.document.title}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </aside>
  );
};
